import { Injectable } from "@angular/core";
import * as moment from "moment-timezone";
import { Observable } from "rxjs/Observable";
import { catchError, map } from "rxjs/operators";
import { APP_SETTINGS } from "../models/backofficeConfiguration";
import { ScheduledContent, ScheduledType } from "../models/ScheduledContent";
import { ErrorHandlerService } from "./errorHandler.service";
import { ExecuteService } from "./executeService.service";

interface ScheduledRequest {
  publicationDateUtc: moment.Moment;
  content: string;
  type: ScheduledType;
}

interface ScheduledResponse {
  id: string;
  publicationDateUtc: moment.Moment;
  content: string;
  type: ScheduledType;
}

@Injectable()
export class ScheduleService {
  constructor(
    private _executeService: ExecuteService,
    private _errorHandler: ErrorHandlerService
  ) {}

  public GetScheduleds<T>(
    type: ScheduledType
  ): Observable<ScheduledContent<T>[]> {
    const url = `${APP_SETTINGS.ScheduledApiUrl}api/v1/Scheduled?type=${type}`;
    return this._executeService.ExecuteWithApiToken("get", url).pipe(
      map((response) => response.map(this.mapScheduledResponse)),
      catchError(
        this._errorHandler.handleError<ScheduledContent<T>[]>(
          "ScheduleService_GetScheduledMessages",
          null,
          true
        )
      )
    );
  }

  public GetScheduledById<T>(id: string): Observable<ScheduledContent<T>> {
    const url = `${APP_SETTINGS.ScheduledApiUrl}api/v1/Scheduled/${id}`;
    return this._executeService.ExecuteWithApiToken("get", url).pipe(
      map((response) => this.mapScheduledResponse<T>(response)),
      catchError(
        this._errorHandler.handleError<ScheduledContent<T>>(
          "ScheduleService_GetScheduledMessage"
        )
      )
    );
  }

  public CreateScheduled<T>(
    scheduled: ScheduledContent<T>
  ): Observable<ScheduledContent<T>> {
    const url = `${APP_SETTINGS.ScheduledApiUrl}api/v1/Scheduled`;
    return this._executeService
      .ExecuteWithApiToken(
        "post",
        url,
        null,
        this.mapScheduledRequest<T>(scheduled)
      )
      .pipe(
        map((response) => this.mapScheduledResponse<T>(response)),
        catchError(
          this._errorHandler.handleError<ScheduledContent<T>>(
            "ScheduleService_CreateScheduledMessage"
          )
        )
      );
  }

  public EditScheduled<T>(
    scheduled: ScheduledContent<T>
  ): Observable<ScheduledContent<T>> {
    const url = `${APP_SETTINGS.ScheduledApiUrl}api/v1/Scheduled/${scheduled.id}`;
    return this._executeService
      .ExecuteWithApiToken(
        "put",
        url,
        null,
        this.mapScheduledRequest<T>(scheduled)
      )
      .pipe(
        map((response) => this.mapScheduledResponse<T>(response)),
        catchError(
          this._errorHandler.handleError<ScheduledContent<T>>(
            "ScheduleService_EditScheduledMessage"
          )
        )
      );
  }

  public DeleteScheduled(id: string): Observable<boolean> {
    const url = `${APP_SETTINGS.ScheduledApiUrl}api/v1/Scheduled/${id}`;
    return this._executeService
      .ExecuteWithApiToken("delete", url)
      .pipe(
        catchError(
          this._errorHandler.handleError<object>(
            "ScheduleService_DeleteScheduledMessage"
          )
        )
      );
  }

  private mapScheduledResponse<T>(
    response: ScheduledResponse
  ): ScheduledContent<T> {
    const result: ScheduledContent<T> = {
      id: response.id,
      content: JSON.parse(response.content),
      dateUtc: moment.utc(response.publicationDateUtc),
      type: response.type,
    };
    return result;
  }

  private mapScheduledRequest<T>(
    scheduled: ScheduledContent<T>
  ): ScheduledRequest {
    const result: ScheduledRequest = {
      content: JSON.stringify(scheduled.content),
      publicationDateUtc: moment.utc(scheduled.dateUtc),
      type: scheduled.type,
    };
    return result;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/schedule.service.ts